import React, { Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';
import Table, { ORDER } from '../Table/Table';
import { TableBodyItem } from '../Table/Table.styles';
import { ReactComponent as ArrowDown } from '../../assets/icons/arrow-down.svg';
import { ReactComponent as ArrowUp } from '../../assets/icons/arrow-up.svg';
import { formatValueByType } from './chartUtils';

const DEFAULT_SORT_OPTIONS = {
  sortBy: 'totalEarnings',
  order: ORDER.DESC,
};

const TABLE_HEADER = [
  { label: 'Creator', key: 'influencerName', sortable: true, fixedWidth: 18 },
  { label: 'Total', key: 'totalEarnings', sortable: true },
  { label: 'Main Flow', key: 'mainFlowEarnings', sortable: true },
  { label: 'Manual', key: 'manualEarnings', sortable: true },
  { label: 'Mass DM', key: 'massDmEarnings', sortable: true },
  { label: 'Prev Total', key: 'previousTotalEarnings', sortable: true },
  { label: 'Trend', key: 'trend', sortable: true },
];

const POSITIVE_TREND_COLOR = '#16A34A';
const NEGATIVE_TREND_COLOR = '#DC2626';

const TrendIndicator = ({ trend }) => {
  if (trend > 0) {
    return (
      <TrendValue $color={POSITIVE_TREND_COLOR}>
        <ArrowUp fill={POSITIVE_TREND_COLOR} width={20} height={20} />
        {formatValueByType(trend, 'percent')}
      </TrendValue>
    );
  }

  if (trend < 0) {
    return (
      <TrendValue $color={NEGATIVE_TREND_COLOR}>
        <ArrowDown fill={NEGATIVE_TREND_COLOR} width={20} height={20} />
        {formatValueByType(trend, 'percent')}
      </TrendValue>
    );
  }

  return <TrendValue>{formatValueByType(trend, 'percent')}</TrendValue>;
};

const OrganizationAnalyticsInfluencerTableItem = ({ item, influencerLabelMap }) => {
  const influencerName = influencerLabelMap[item.influencerId] || item.influencerId || '-';

  return (
    <Fragment>
      <TableBodyItem>
        <TableValue $isName>{influencerName}</TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TableValue>{formatValueByType(item.totalEarnings, 'currency')}</TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TableValue>
          {formatValueByType(item.mainFlowEarnings, 'currency')} (
          {formatValueByType(item.mainFlowPercentage, 'percent')})
        </TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TableValue>
          {formatValueByType(item.manualEarnings, 'currency')} ({formatValueByType(item.manualPercentage, 'percent')})
        </TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TableValue>
          {formatValueByType(item.massDmEarnings, 'currency')} ({formatValueByType(item.massDmPercentage, 'percent')})
        </TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TableValue>{formatValueByType(item.previousTotalEarnings, 'currency')}</TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TrendIndicator trend={item.trend} />
      </TableBodyItem>
    </Fragment>
  );
};

const OrganizationAnalyticsInfluencerTable = ({ data, pending, influencerLabelMap = {} }) => {
  const [sortOptions, setSortOptions] = useState(DEFAULT_SORT_OPTIONS);

  const normalizedData = useMemo(() => {
    const influencerIds = data?.influencerIds || [];

    return influencerIds.map((influencerId, index) => {
      const mainFlowEarnings = Number(data?.mainFlowEarnings?.[index] || 0);
      const manualEarnings = Number(data?.manualEarnings?.[index] || 0);
      const massDmEarnings = Number(data?.massDmEarnings?.[index] || 0);
      const totalEarnings = Number(data?.totalEarnings?.[index] || 0);
      const mainFlowPercentage = Number(data?.mainFlowPercentage?.[index] || 0);
      const manualPercentage = Number(data?.manualPercentage?.[index] || 0);
      const massDmPercentage = Number(data?.massDmPercentage?.[index] || 0);
      const previousTotalEarnings = Number(data?.previousTotalEarnings?.[index] || 0);
      const trend = Number(data?.trend?.[index] || 0);

      return {
        influencerId,
        mainFlowEarnings,
        manualEarnings,
        massDmEarnings,
        totalEarnings,
        mainFlowPercentage,
        manualPercentage,
        massDmPercentage,
        previousTotalEarnings,
        trend,
      };
    });
  }, [data]);

  const sortedData = useMemo(() => {
    const items = [...normalizedData];

    return items.sort((firstItem, secondItem) => {
      const firstInfluencerName = influencerLabelMap[firstItem.influencerId] || firstItem.influencerId || '';
      const secondInfluencerName = influencerLabelMap[secondItem.influencerId] || secondItem.influencerId || '';

      const firstValue = sortOptions.sortBy === 'influencerName' ? firstInfluencerName : firstItem[sortOptions.sortBy];
      const secondValue =
        sortOptions.sortBy === 'influencerName' ? secondInfluencerName : secondItem[sortOptions.sortBy];

      const comparison =
        typeof firstValue === 'string' || typeof secondValue === 'string'
          ? String(firstValue || '').localeCompare(String(secondValue || ''))
          : Number(firstValue || 0) - Number(secondValue || 0);

      return sortOptions.order === ORDER.ASC ? comparison : comparison * -1;
    });
  }, [influencerLabelMap, normalizedData, sortOptions]);

  return (
    <Table
      headerInformation={TABLE_HEADER}
      dataLoading={pending}
      totalCount={sortedData.length}
      currentPage={1}
      perPage={Math.max(sortedData.length, 1)}
      setPerPage={() => {}}
      setPage={() => {}}
      sortOptions={sortOptions}
      setSortOptions={setSortOptions}
      emptyDataText="Select one or more creators to load table data."
      hideFooter>
      {sortedData.map(item => (
        <OrganizationAnalyticsInfluencerTableItem
          key={item.influencerId}
          item={item}
          influencerLabelMap={influencerLabelMap}
        />
      ))}
    </Table>
  );
};

const TableValue = styled.p`
  font-weight: ${props => (props.$isName ? 500 : 300)};
`;

const TrendValue = styled(TableValue)`
  align-items: center;
  color: ${props => props.$color};
  display: inline-flex;
  gap: 0.4rem;
  font-weight: 400;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
`;

export default OrganizationAnalyticsInfluencerTable;
